import { AbstractConnectorArguments, ConnectorUpdate } from '@web3-react/types';
import { AbstractConnector } from '@web3-react/abstract-connector';
import warning from 'tiny-warning';

import { SendReturnResult, SendReturn, Send, SendOld } from './types';

interface Ethereum {
    send: unknown;
    enable: () => Promise<string[]>;
    on?: (method: string, listener: (...args: any[]) => void) => void;
    removeListener?: (method: string, listener: (...args: any[]) => void) => void;
}

declare global {
    interface Window {
        NaboxWallet?: Ethereum;
        isNabox?: boolean;
    }
}

function parseSendReturn(sendReturn: SendReturnResult | SendReturn): any {
    return sendReturn.hasOwnProperty('result') ? sendReturn.result : sendReturn;
}

export class NoEthereumProviderError extends Error {
    public constructor() {
        super();
        this.name = this.constructor.name;
        this.message = 'No Ethereum provider was found on window.NaboxWallet.';
    }
}

export class UserRejectedRequestError extends Error {
    public constructor() {
        super();
        this.name = this.constructor.name;
        this.message = 'The user rejected the request.';
    }
}

export class NaboxConnector extends AbstractConnector {
    constructor(kwargs: AbstractConnectorArguments) {
        super(kwargs);

        this.handleNetworkChanged = this.handleNetworkChanged.bind(this);
        this.handleChainChanged = this.handleChainChanged.bind(this);
        this.handleAccountsChanged = this.handleAccountsChanged.bind(this);
        this.handleClose = this.handleClose.bind(this);
    }

    private handleChainChanged(chainId: string | number): void {
        this.emitUpdate({ chainId, provider: window.NaboxWallet });
    }

    private handleAccountsChanged(accounts: string[]): void {
        if (accounts.length === 0) {
            this.emitDeactivate();
        } else {
            this.emitUpdate({ account: accounts[0] });
        }
    }

    private handleClose(code: number, reason: string): void {
        this.emitDeactivate();
    }

    private handleNetworkChanged(networkId: string | number): void {
        this.emitUpdate({ chainId: networkId, provider: window.NaboxWallet });
    }

    public async activate(): Promise<ConnectorUpdate> {
        if (!window.NaboxWallet) {
            throw new NoEthereumProviderError();
        }

        if (window.NaboxWallet.on) {
            window.NaboxWallet.on('chainChanged', this.handleChainChanged);
            window.NaboxWallet.on('accountsChanged', this.handleAccountsChanged);
            window.NaboxWallet.on('close', this.handleClose);
            window.NaboxWallet.on('networkChanged', this.handleNetworkChanged);
        }

        // try to activate + get account via eth_requestAccounts
        let account;
        try {
            account = await (window.NaboxWallet.send as Send)('eth_requestAccounts').then(
                (sendReturn) => parseSendReturn(sendReturn)[0]
            );
        } catch (error) {
            if ((error as any).code === 4001) {
                throw new UserRejectedRequestError();
            }
            warning(
                false,
                'eth_requestAccounts was unsuccessful, falling back to enable'
            );
        }

        // if unsuccessful, try enable
        if (!account) {
            // if enable is successful but doesn't return accounts, fall back to getAccount (not happy i have to do this...)
            account = await window.NaboxWallet.enable().then(
                (sendReturn) => sendReturn && parseSendReturn(sendReturn)[0]
            );
        }

        return { provider: window.NaboxWallet, ...(account ? { account } : {}) };
    }

    public async getProvider(): Promise<any> {
        return window.NaboxWallet;
    }

    public async getChainId(): Promise<number | string> {
        if (!window.NaboxWallet) {
            throw new NoEthereumProviderError();
        }

        let chainId;
        try {
            chainId = await (window.NaboxWallet.send as Send)('eth_chainId').then(
                parseSendReturn
            );
        } catch {
            warning(false, 'eth_chainId was unsuccessful, falling back to net_version');
        }

        if (!chainId) {
            try {
                chainId = await (window.NaboxWallet.send as Send)('net_version').then(
                    parseSendReturn
                );
            } catch {
                warning(
                    false,
                    'net_version was unsuccessful, falling back to net version v2'
                );
            }
        }

        if (!chainId) {
            try {
                chainId = parseSendReturn(
                    (window.NaboxWallet.send as SendOld)({ method: 'net_version' })
                );
            } catch {
                warning(
                    false,
                    'net_version v2 was unsuccessful, falling back to manual matches and static properties'
                );
            }
        }

        if (!chainId) {
            if ((window.NaboxWallet as any).isDapper) {
                chainId = parseSendReturn(
                    (window.NaboxWallet as any).cachedResults.net_version
                );
            } else {
                chainId =
                    (window.NaboxWallet as any).chainId ||
                    (window.NaboxWallet as any).netVersion ||
                    (window.NaboxWallet as any).networkVersion ||
                    (window.NaboxWallet as any)._chainId;
            }
        }

        return chainId;
    }

    public async getAccount(): Promise<null | string> {
        if (!window.NaboxWallet) {
            throw new NoEthereumProviderError();
        }

        let account;
        try {
            account = await (window.NaboxWallet.send as Send)('eth_accounts').then(
                (sendReturn) => parseSendReturn(sendReturn)[0]
            );
        } catch {
            warning(false, 'eth_accounts was unsuccessful, falling back to enable');
        }

        if (!account) {
            try {
                account = await window.NaboxWallet.enable().then(
                    (sendReturn) => parseSendReturn(sendReturn)[0]
                );
            } catch {
                warning(
                    false,
                    'enable was unsuccessful, falling back to eth_accounts v2'
                );
            }
        }

        if (!account) {
            account = parseSendReturn(
                (window.NaboxWallet.send as SendOld)({ method: 'eth_accounts' })
            )[0];
        }

        return account;
    }

    public deactivate() {
        if (window.NaboxWallet && window.NaboxWallet.removeListener) {
            window.NaboxWallet.removeListener('chainChanged', this.handleChainChanged);
            window.NaboxWallet.removeListener(
                'accountsChanged',
                this.handleAccountsChanged
            );
            window.NaboxWallet.removeListener('close', this.handleClose);
            window.NaboxWallet.removeListener(
                'networkChanged',
                this.handleNetworkChanged
            );
        }
    }

    public async isAuthorized(): Promise<boolean> {
        if (!window.NaboxWallet) {
            return false;
        }

        try {
            return await (window.NaboxWallet.send as Send)('eth_accounts').then(
                (sendReturn) => {
                    if (parseSendReturn(sendReturn).length > 0) {
                        return true;
                    } else {
                        return false;
                    }
                }
            );
        } catch {
            return false;
        }
    }
}
